import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext, SttTable, SttTopControls } from '../../../sporttia/all';
import { useCrud, useToggle } from '../../../lib/hooks';
import ModalCategory from './ModalCategory';
import { copyToClipboard, parsePrice } from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type Mandatory = {
    id: string | number;
    name?: string;
};

type Category = {
    id?: string | number;
    name: string;
    description?: string;
    enrollment?: number | string;
    maxParticipants?: number | string;
    maxTeams?: number | string;
    mandatory?: Mandatory;
    numTeams?: number;
    numParticipants?: number;
    trash?: boolean;
};

type Event = {
    id: string | number;
    name: string;
    teamable: boolean;
};

type CategoriesData = {
    count: number;
    rows: Category[];
};

type Props = {
    items?: Category[];
    onChange?: (categories: Category[]) => void;
    event: Event;
};

export default function EventCategories({
    items,
    onChange,
    event,
}: Props): React.ReactElement | null {
    const [categories, setCategories, , Get, Post, Put, Delete] =
        useCrud<CategoriesData>();
    const [selectedCat, setSelectedCat] = useState<Category | undefined>();
    const [trash, toggleTrash] = useToggle(() => {
        loadCategories();
    });

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    useEffect(() => {
        if (items) {
            setCategories({
                count: items.length,
                rows: items,
            });
        }
    }, [items]);

    function loadCategories(
        params: { page?: number; rows?: number } = { page: 1, rows: 25 },
    ): void {
        Get(`/events/${event.id}/categories`, { ...params, trash }).then(
            (result: any) => {
                if (result.rows) {
                    setCategories(result as CategoriesData);
                }
            },
        );
    }

    function saveCategory(category: Category): void {
        const params = {
            name: category.name,
            description: category.description,
            enrollment: category.enrollment
                ? parsePrice(category.enrollment as string)
                : 0,
            maxParticipants: category.maxParticipants
                ? parseInt(category.maxParticipants as string, 10)
                : 0,
            maxTeams: category.maxTeams
                ? parseInt(category.maxTeams as string, 10)
                : 0,
            idMandatory: category?.mandatory ? category.mandatory.id : null,
        };

        // Put or Post depending on existing id
        if (category.id) {
            Put(`/events/categories/${category.id}`, params)
                .then((response: unknown) => {
                    const cat = (response as { category: Category }).category;
                    const newCategories = {
                        ...categories,
                        rows: categories.rows.map((c: any) =>
                            c.id === cat.id ? cat : c,
                        ),
                    };
                    setCategories(newCategories);
                    setSelectedCat(undefined);
                    if (onChange) {
                        onChange(newCategories.rows);
                    }
                })
                .catch(() => {});
        } else {
            Post(`/events/${event.id}/categories`, params)
                .then((response: unknown) => {
                    const cat = (response as { category: Category }).category;
                    const newCategories = {
                        ...categories,
                        rows: [...categories.rows, cat],
                    };
                    setCategories(newCategories);
                    setSelectedCat(undefined);
                    if (onChange) {
                        onChange(newCategories.rows);
                    }
                })
                .catch(() => {});
        }
    }

    function deleteCategory(category: Category): void {
        Delete(`/events/categories/${category.id}`)
            .then(() => {
                setSelectedCat(undefined);
                loadCategories();
                if (onChange) {
                    onChange(
                        categories.rows.filter(
                            (row: any) => row.id !== category.id,
                        ),
                    );
                }
            })
            .catch(() => {});
    }

    function recoverCategory(cat: Category): void {
        Put(`/events/categories/${cat.id}/recover`)
            .then(() => {
                const newCategories = {
                    ...categories,
                    rows: categories.rows.map((c: any) =>
                        c.id === cat.id ? { ...c, trash: false } : c,
                    ),
                };
                setCategories(newCategories);
                setSelectedCat(undefined);
                if (onChange) {
                    onChange(newCategories.rows);
                }
            })
            .catch(() => {});
    }

    if (!categories) {
        return null;
    }

    return (
        <>
            <SttTopControls
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.EVENT.MODIFY,
                    caption: t('Create'),
                    onClick: () =>
                        setSelectedCat({
                            name: '',
                            id: 0,
                        }),
                }}
                onToggleTrash={toggleTrash}
                pl={0}
            />
            <SttTable
                autoload={false}
                data={categories}
                onFetch={loadCategories}
                columns={[
                    {
                        title: '#',
                        type: 'tooltip',
                        value: (row: Category) => ({
                            label: '#',
                            value: row.id,
                        }),
                        onClick: (row: Category) => {
                            copyToClipboard(row.id as string, () =>
                                cxt?.showMessage('S', t('copiedToClipboard')),
                            );
                        },
                    },
                    {
                        title: t('Name'),
                        type: 'link',
                        field: 'name',
                        align: 'left',
                        onClick: (row: Category) => setSelectedCat(row),
                    },
                    {
                        title: t('Enrollment'),
                        type: 'price',
                        field: 'enrollment',
                        align: 'center',
                    },
                    {
                        title: event.teamable ? t('Teams') : t('Participants'),
                        value: (row: Category) =>
                            event.teamable
                                ? `${row.numTeams} / ${row.maxTeams || '∞'}`
                                : `${row.numParticipants} / ${
                                      row.maxParticipants || '∞'
                                  }`,
                    },
                    {
                        title: t('MandatoryGroup'),
                        value: (row: Category) =>
                            row.mandatory && row.mandatory.name,
                        align: 'right',
                    },
                ]}
            />
            <ModalCategory
                item={selectedCat as any}
                eventName={event.name}
                teamable={event.teamable}
                onClose={() => setSelectedCat(undefined)}
                onSave={saveCategory as any}
                onDelete={deleteCategory as any}
                onRecover={recoverCategory as any}
            />
        </>
    );
}
